import { useCallback } from 'react';
import { useConfigInputs, useConfigActions, useVendorCtx } from 'shared/state/configContexts';

/** The one system-wide sizing mode: both storage sets are sized by node count,
 *  or both by minimum raw capacity. `ssdPerVpod` is read off productDb, as the
 *  classic BaselineConfigCard.jsx:67 reads it. */
export function useSizingMode() {
  const { config } = useConfigInputs();
  const { updateConfig } = useConfigActions();
  const { productDb } = useVendorCtx();
  const ssdPerVpod: number = productDb?.ssdPerVpod ?? 0;

  // R15: the raw-capacity seed is per SET (isFlash), not one formula applied to
  // both node counts — that silently dropped the JBOD term for hybrid, or added
  // a phantom one for flash. Matches the classic per-set helper:
  // BaselineConfigCard.jsx:65-71 takes (nodeCount, storageType) and its handler
  // (:80-84) calls it once per set. Every field read is listed explicitly, so no
  // exhaustive-deps disable.
  const onSizeByChange = useCallback((mode: string) => {
    if (mode === 'nodeCount') { updateConfig({ sizeByNodeCount: true }); return; }
    const rawTb = (count: number, isFlash: boolean) => isFlash
      ? count * ssdPerVpod * config.afaVpodSsdCapacity
      : count * ssdPerVpod * config.hybridVpodSsdCapacity + count * config.jbodSize * config.hybridHddCapacity;
    updateConfig({
      sizeByNodeCount: false,
      hybridMinRawCapacity: rawTb(config.hybridNodeCount, false),
      afaMinRawCapacity: rawTb(config.afaNodeCount, true),
    });
  }, [
    updateConfig, ssdPerVpod,
    config.afaVpodSsdCapacity, config.hybridVpodSsdCapacity, config.jbodSize, config.hybridHddCapacity,
    config.hybridNodeCount, config.afaNodeCount,
  ]);

  return { sizeBy: config.sizeByNodeCount ? 'nodeCount' : 'capacity', onSizeByChange };
}
